import React, { useState, useEffect } from "react";
import { getBoard, createCard, updateCard, deleteCard, getCardMetrics } from "../api/services";
import { Loading, Button } from "../components/common/UI";
import KanbanBoard from "../components/board/KanbanBoard";
import Modal from "../components/common/Modal";
import { IconTrash } from "../components/common/Icons";
import styles from "./pages.module.css";

const OUTCOMES = ["", "confirmed", "rejected", "inconclusive"];
const OUTCOME_LABEL = { "": "— нет —", confirmed: "подтверждена", rejected: "отклонена", inconclusive: "неясно" };

// Список ISO-недель для назначения эксперимента: прошлая + текущая + до конца октября
function isoWeekOf(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return { year: date.getUTCFullYear(), week };
}

// Вся работа акселерации начинается с 28-й недели 2026 — раньше не показываем
const FIRST_WORK_WEEK = "2026-W28";

function weekOptions() {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7)); // понедельник текущей недели
  const fmt = (d) => `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
  const out = [{ key: "", label: "— не назначена —" }];
  for (let i = 0; i < 18; i++) {
    const mon = new Date(monday);
    mon.setDate(monday.getDate() + i * 7);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    const { year, week } = isoWeekOf(mon);
    const key = `${year}-W${String(week).padStart(2, "0")}`;
    if (key < FIRST_WORK_WEEK) continue;
    const nw = isoWeekOf(now);
    const nowKey = `${nw.year}-W${String(nw.week).padStart(2, "0")}`;
    const mark = key === nowKey ? " · текущая" : "";
    out.push({ key, label: `Неделя ${week} · ${fmt(mon)}–${fmt(sun)}${mark}` });
  }
  return out;
}

const WEEKS = weekOptions();

// Read-only блок с описанием гипотезы внутри карточки эксперимента
function HypothesisInfo({ hyp }) {
  if (!hyp) return null;
  const rows = [
    ["Формулировка", hyp.statement],
    ["Метрика успеха", hyp.success_metric],
    ["Guardrail (не должно просесть)", hyp.guardrail_metric],
    ["Ожидаемый результат", hyp.expected_result],
    ["Как измеряем", hyp.how_to_measure],
    ["Ресурсы", hyp.resources],
  ].filter(([, v]) => v && v.trim());
  if (!rows.length) return null;
  return (
    <div style={{
      background: "var(--bg-elev)", border: "1px solid var(--border)",
      borderRadius: 8, padding: "10px 12px", marginBottom: 14,
    }}>
      {rows.map(([label, value]) => (
        <div key={label} style={{ marginBottom: 8 }}>
          <div style={{ color: "var(--text-mute)", fontSize: 11.5 }}>{label}</div>
          <div style={{ color: "var(--text-dim)", fontSize: 13, lineHeight: 1.5 }}>{value}</div>
        </div>
      ))}
      <div style={{ color: "var(--text-mute)", fontSize: 11 }}>
        Правится в разделе «Гипотезы»
      </div>
    </div>
  );
}

// Модалка метрик эксперимента: универсальный рендер блоков от бэка
function MetricsBlocks({ data }) {
  if (!data) return null;
  if (!data.available) {
    return <div style={{ color: "var(--text-dim)", fontSize: 13.5, lineHeight: 1.6 }}>{data.message}</div>;
  }
  return (
    <div>
      {data.subtitle && (
        <div style={{ color: "var(--text-mute)", fontSize: 12, lineHeight: 1.55, marginBottom: 12 }}>
          {data.subtitle}
        </div>
      )}
      {data.success_metric && (
        <div style={{
          background: "var(--accent-soft)", borderRadius: 8,
          padding: "8px 12px", marginBottom: 14, fontSize: 12.5,
        }}>
          <span style={{ color: "var(--text-mute)" }}>Метрика успеха: </span>
          <span style={{ color: "var(--text)" }}>{data.success_metric}</span>
        </div>
      )}
      {(data.blocks || []).map((b, i) => {
        if (b.type === "kpi") {
          return (
            <div key={i} style={{
              display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
              gap: 10, marginBottom: 16,
            }}>
              {b.items.map((it) => (
                <div key={it.label} style={{
                  background: "var(--bg-elev)", border: "1px solid var(--border)",
                  borderRadius: 10, padding: "10px 12px",
                }}>
                  <div style={{ color: "var(--text-mute)", fontSize: 11.5 }}>{it.label}</div>
                  <div style={{ color: "var(--text)", fontSize: 20, fontWeight: 700, margin: "2px 0" }}>
                    {it.value}
                  </div>
                  {it.hint && <div style={{ color: "var(--text-dim)", fontSize: 11.5 }}>{it.hint}</div>}
                </div>
              ))}
            </div>
          );
        }
        if (b.type === "table") {
          return (
            <div key={i} style={{ marginBottom: 16 }}>
              <div style={{ color: "var(--text)", fontSize: 13, fontWeight: 600, marginBottom: 7 }}>
                {b.title}
              </div>
              <div style={{ border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ background: "var(--bg-elev)" }}>
                      {b.columns.map((c) => (
                        <th key={c} style={{
                          textAlign: "left", padding: "7px 10px",
                          color: "var(--text-mute)", fontWeight: 600,
                        }}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((row, ri) => (
                      <tr key={ri} style={{ borderTop: "1px solid var(--border)" }}>
                        {row.map((cell, ci) => (
                          <td key={ci} style={{ padding: "6px 10px", color: "var(--text-dim)" }}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }
        if (b.type === "note") {
          return (
            <div key={i} style={{ color: "var(--text-mute)", fontSize: 12, lineHeight: 1.55, marginBottom: 10 }}>
              {b.text}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

export default function Board() {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  // Модалка «Метрики»: {card, data|null (грузится), error}
  const [metricsModal, setMetricsModal] = useState(null);

  const openMetrics = async (card) => {
    setMetricsModal({ card, data: null, error: null });
    try {
      const data = await getCardMetrics(card.id);
      setMetricsModal((prev) => (prev && prev.card.id === card.id ? { ...prev, data } : prev));
    } catch (e) {
      setMetricsModal((prev) => (prev && prev.card.id === card.id
        ? { ...prev, error: "Не удалось загрузить метрики" } : prev));
    }
  };

  const load = async () => {
    setLoading(true);
    const d = await getBoard();
    setBoard(d);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("ingest-done", h);
    return () => window.removeEventListener("ingest-done", h);
  }, []);

  const move = async (cardId, toColumn) => {
    // оптимистично
    setBoard((prev) => {
      const cols = prev.columns.map((c) => ({ ...c, cards: [...c.cards] }));
      let moved = null;
      for (const col of cols) {
        const idx = col.cards.findIndex((c) => String(c.id) === String(cardId));
        if (idx >= 0) {
          moved = col.cards.splice(idx, 1)[0];
          break;
        }
      }
      if (moved) {
        moved.column_key = toColumn;
        cols.find((c) => c.key === toColumn)?.cards.push(moved);
      }
      return { ...prev, columns: cols };
    });
    try {
      await updateCard(cardId, { column_key: toColumn });
    } catch {
      load(); // откат к серверному состоянию
    }
  };

  const addCard = async (columnKey) => {
    await createCard({ title: "Новый эксперимент", column_key: columnKey });
    load();
  };

  const saveCard = async () => {
    const { id, ...body } = modal;
    await updateCard(id, {
      title: body.title, column_key: body.column_key,
      week_iso: body.week_iso || null, result_note: body.result_note,
      outcome: body.outcome || null,
    });
    setModal(null);
    load();
  };

  const removeCard = async () => {
    await deleteCard(modal.id);
    setModal(null);
    load();
  };

  if (loading || !board) return <Loading />;

  return (
    <div className={styles.page}>
      <div className={styles.pageHead}>
        <div>
          <h1 className={styles.pageTitle}>Канбан экспериментов</h1>
          <div className={styles.pageSub}>Недельные спринты гипотез. Перетаскивай карточки между колонками.</div>
        </div>
      </div>

      <KanbanBoard
        columns={board.columns}
        onMove={move}
        onAdd={addCard}
        onCardClick={(card) => setModal({ ...card, outcome: card.outcome || "" })}
        onCardMetrics={openMetrics}
      />

      {metricsModal && (
        <Modal
          wide
          title={`Метрики: ${metricsModal.card.title}`}
          onClose={() => setMetricsModal(null)}
          footer={<Button variant="ghost" onClick={() => setMetricsModal(null)}>Закрыть</Button>}
        >
          {metricsModal.error && (
            <div style={{ color: "var(--red)", fontSize: 13 }}>{metricsModal.error}</div>
          )}
          {!metricsModal.error && !metricsModal.data && <Loading />}
          {metricsModal.data && <MetricsBlocks data={metricsModal.data} />}
        </Modal>
      )}

      {modal && (
        <Modal
          title="Карточка эксперимента"
          onClose={() => setModal(null)}
          footer={
            <>
              <Button variant="danger" onClick={removeCard}>
                <IconTrash width={15} height={15} />
                {modal.hypothesis_id ? " В бэклог" : " Удалить"}
              </Button>
              <Button variant="ghost" onClick={() => setModal(null)}>Отмена</Button>
              <Button onClick={saveCard}>Сохранить</Button>
            </>
          }
        >
          <div className={styles.field}>
            <label>Название</label>
            <input value={modal.title} onChange={(e) => setModal({ ...modal, title: e.target.value })} />
          </div>
          <HypothesisInfo hyp={modal.hypothesis} />
          <div className={styles.field}>
            <label>Неделя эксперимента</label>
            <select value={modal.week_iso || ""} onChange={(e) => setModal({ ...modal, week_iso: e.target.value })}>
              {WEEKS.map((w) => (
                <option key={w.key} value={w.key}>{w.label}</option>
              ))}
              {modal.week_iso && !WEEKS.some((w) => w.key === modal.week_iso) && (
                <option value={modal.week_iso}>{modal.week_iso}</option>
              )}
            </select>
          </div>
          <div className={styles.field}>
            <label>Итог эксперимента</label>
            <textarea rows={3} value={modal.result_note || ""} onChange={(e) => setModal({ ...modal, result_note: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>Результат</label>
            <select value={modal.outcome || ""} onChange={(e) => setModal({ ...modal, outcome: e.target.value })}>
              {OUTCOMES.map((o) => <option key={o} value={o}>{OUTCOME_LABEL[o]}</option>)}
            </select>
          </div>
        </Modal>
      )}
    </div>
  );
}
